/**
 * PATH: packages/email-hub/src/feature/components/inbox-command/InboxRefineMenu.tsx
 * FILE_ID: EMAIL-INBOX-REFINE-MENU-001
 * PURPOSE: Keep secondary Inbox controls in one stable, scroll-safe overlay.
 * WHY: Refinement must not create toolbar rows or close while its own content scrolls.
 */

import {
  Eye,
  EyeOff,
  Filter,
  Keyboard,
  Layers,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from 'lucide-react'
import { type ReactNode, useMemo, useRef, useState } from 'react'
import { AdaptiveOverlay } from '@quotech/ui'

import { cn } from '../../../_internal/lib/utils'
import type { PersonalCategoryLike } from '../../lib/categoryConfig'
import { getCategoryConfig, isPromoSelected } from '../../lib/categoryConfig'
import {
  EMAIL_SORT_OPTIONS,
  type EmailSortMode,
} from '../../lib/sortEmails'
import { buildVisibleKeys } from '../category-filter-bar/fitCategoryChips'

interface InboxRefineMenuProps {
  activeCount: number
  sortMode: EmailSortMode
  onSortMode: (mode: EmailSortMode) => void
  canGroupByMailbox: boolean
  groupByMailbox: boolean
  onToggleGroupByMailbox: () => void
  showSidebar: boolean
  onToggleSidebar: () => void
  advancedFilters: ReactNode
  onOpenShortcuts: () => void
  smartSearch: boolean
  onToggleSmartSearch: () => void
  byCategory: Record<string, number> | undefined
  selectedCategories: string[]
  mutedCategories: string[]
  customCategories: PersonalCategoryLike[]
  onToggleCategory: (key: string) => void
  onClearCategories: () => void
  onApplyImportantPreset: () => void
  onUnmuteCategory: (key: string) => void
  allocation: 'allocated' | 'unallocated' | undefined
  onSelectAllocation: (
    value: 'allocated' | 'unallocated' | undefined,
  ) => void
  hidePromotions: boolean
  onToggleHidePromotions: (next: boolean) => void
  hideCleared: boolean
  onToggleHideCleared: (next: boolean) => void
  classifyBusy: boolean
  onClassify: () => void
  onRecategorize: () => void
}

export function InboxRefineMenu({
  activeCount,
  sortMode,
  onSortMode,
  canGroupByMailbox,
  groupByMailbox,
  onToggleGroupByMailbox,
  showSidebar,
  onToggleSidebar,
  advancedFilters,
  onOpenShortcuts,
  smartSearch,
  onToggleSmartSearch,
  byCategory,
  selectedCategories,
  mutedCategories,
  customCategories,
  onToggleCategory,
  onClearCategories,
  onApplyImportantPreset,
  onUnmuteCategory,
  allocation,
  onSelectAllocation,
  hidePromotions,
  onToggleHidePromotions,
  hideCleared,
  onToggleHideCleared,
  classifyBusy,
  onClassify,
  onRecategorize,
}: InboxRefineMenuProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const buttonRef = useRef<HTMLButtonElement>(null)
  const visibleKeys = useMemo(
    () => buildVisibleKeys(byCategory, selectedCategories, mutedCategories),
    [byCategory, mutedCategories, selectedCategories],
  )
  const categoryKeys = useMemo(
    () => [...new Set([...visibleKeys, ...mutedCategories])],
    [mutedCategories, visibleKeys],
  )
  const promoSelected = isPromoSelected(selectedCategories)
  const filteredKeys = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return categoryKeys
    return categoryKeys.filter((key) => {
      const label = getCategoryConfig(key, customCategories).label.toLowerCase()
      return key.toLowerCase().includes(normalized) || label.includes(normalized)
    })
  }, [categoryKeys, customCategories, query])

  const toggle = (
    checked: boolean,
    label: string,
    onClick: () => void,
    icon: typeof Filter,
  ) => {
    const Icon = icon
    return (
      <button
        type="button"
        aria-pressed={checked}
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-xs',
          'hover:bg-accent',
          checked && 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
        <span className="flex-1">{label}</span>
        <span aria-hidden>{checked ? '✓' : ''}</span>
      </button>
    )
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setQuery('')
          setOpen((current) => !current)
        }}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Refine this inbox"
        title="Refine this inbox"
        className={cn(
          'relative inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border px-2',
          'text-xs transition-colors',
          open || activeCount > 0
            ? 'border-primary/40 bg-primary/10 text-primary'
            : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
        data-testid="inbox-refine-button"
      >
        <Filter className="h-3.5 w-3.5" />
        {activeCount > 0 && (
          <span className="min-w-4 rounded-full bg-primary px-1 text-[9px] font-bold text-primary-foreground">
            {activeCount}
          </span>
        )}
      </button>

      <AdaptiveOverlay
        open={open}
        onOpenChange={setOpen}
        anchorRef={buttonRef}
        mode="auto"
        autoSheetBelowPx={1024}
        label="Refine inbox"
        testId="inbox-refine-menu"
      >
            <div className="flex items-center justify-between px-1 pb-2">
              <div>
                <p className="text-sm font-semibold">Refine inbox</p>
                <p className="text-[10px] text-muted-foreground">
                  Categories, order, grouping and tools
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                aria-label="Close refinement menu"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <label className="mb-2 flex items-center gap-2 rounded-md border border-border px-2 py-1.5">
              <Search className="h-3.5 w-3.5 text-muted-foreground" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a category"
                className="min-w-0 flex-1 bg-transparent text-xs outline-none"
              />
            </label>

            <div className="rounded-lg bg-muted/30 p-1">
              {toggle(
                showSidebar,
                'Detailed filters',
                onToggleSidebar,
                Filter,
              )}
              {toggle(
                smartSearch,
                'Smart semantic search',
                onToggleSmartSearch,
                Sparkles,
              )}
              {canGroupByMailbox
                && toggle(
                  groupByMailbox,
                  'Group by mailbox',
                  onToggleGroupByMailbox,
                  Layers,
                )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  onOpenShortcuts()
                }}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-xs text-foreground hover:bg-accent"
                title="Keyboard shortcuts (?)"
              >
                <Keyboard className="h-3.5 w-3.5 shrink-0 text-foreground" />
                <span className="flex-1 text-left">Keyboard shortcuts</span>
                <kbd
                  className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-background px-1 text-[10px] font-mono font-semibold text-foreground"
                  aria-hidden
                >
                  ?
                </kbd>
              </button>
            </div>

            {showSidebar && (
              <div className="mt-2 max-h-[45vh] overflow-hidden rounded-lg border border-border [&>div]:h-[45vh] [&>div]:w-full [&>div]:border-r-0">
                {advancedFilters}
              </div>
            )}

            <div className="mt-2 rounded-lg bg-muted/30 p-1">
              <label className="flex items-center gap-2 rounded-md px-2.5 py-2 text-xs">
                <Filter className="h-3.5 w-3.5" />
                <span className="flex-1">Allocation</span>
                <select
                  value={allocation ?? ''}
                  onChange={(event) =>
                    onSelectAllocation(
                      event.target.value
                        ? event.target.value as 'allocated' | 'unallocated'
                        : undefined,
                    )
                  }
                  className="rounded border border-border bg-background px-1.5 py-1 text-xs"
                  aria-label="Allocation filter"
                >
                  <option value="">All</option>
                  <option value="allocated">Allocated</option>
                  <option value="unallocated">Unallocated</option>
                </select>
              </label>
              {!promoSelected && toggle(
                  !hidePromotions,
                  'Show promotions',
                  () => onToggleHidePromotions(!hidePromotions),
                  hidePromotions ? EyeOff : Eye,
                )}
              {toggle(
                !hideCleared,
                'Show cleared emails',
                () => onToggleHideCleared(!hideCleared),
                hideCleared ? EyeOff : Eye,
              )}
              <button
                type="button"
                onClick={onClassify}
                disabled={classifyBusy}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-xs hover:bg-accent disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Categorize new mail
              </button>
              <button
                type="button"
                onClick={onRecategorize}
                disabled={classifyBusy}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-xs hover:bg-accent disabled:opacity-50"
              >
                <RefreshCw
                  className={cn('h-3.5 w-3.5', classifyBusy && 'animate-spin')}
                />
                Re-categorize using current rules
              </button>
            </div>

            <label className="mt-2 block px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Order
              <select
                value={sortMode}
                onChange={(event) =>
                  onSortMode(event.target.value as EmailSortMode)
                }
                className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-xs font-normal normal-case tracking-normal text-foreground"
              >
                {EMAIL_SORT_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <div className="mt-3 flex items-center justify-between px-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Categories
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onApplyImportantPreset}
                  className="text-[10px] text-primary hover:underline"
                >
                  Important only
                </button>
                {selectedCategories.length > 0 && (
                <button
                  type="button"
                  onClick={onClearCategories}
                  className="text-[10px] text-primary hover:underline"
                >
                  Clear
                </button>
                )}
              </div>
            </div>
            <div className="mt-1 space-y-0.5">
              {filteredKeys.map((key) => {
                const config = getCategoryConfig(key, customCategories)
                const Icon = config.icon
                const selected = selectedCategories.includes(key)
                const muted = mutedCategories.includes(key)
                return (
                  <div
                    key={key}
                    className={cn(
                      'flex w-full items-center gap-1 rounded-md text-xs hover:bg-accent',
                      selected && 'bg-primary/10 text-primary',
                    )}
                  >
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() => onToggleCategory(key)}
                      className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left"
                    >
                      <span className={cn('h-2 w-2 rounded-full', config.dot)} />
                      <Icon className="h-3.5 w-3.5" />
                      <span className="min-w-0 flex-1 truncate">
                        {config.label}
                      </span>
                    </button>
                    {muted && (
                      <button
                        type="button"
                        onClick={() => onUnmuteCategory(key)}
                        className="mr-1 rounded px-1.5 py-1 text-[10px] text-primary hover:bg-primary/10"
                        title={`Show ${config.label} in the inbox`}
                      >
                        Unhide
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
      </AdaptiveOverlay>
    </>
  )
}
